import { Component, inject, signal } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AdminViewAsBannerComponent } from '../../../app/shared/components/admin-view-as-banner/admin-view-as-banner.component';
import { environment } from '../../../environments/environment';
import { MpsUserService } from '../../services/mps-user.service';
import { MpsFooterComponent } from '../mps-footer/mps-footer.component';
import { MpsHeaderComponent } from '../mps-header/mps-header.component';
import { MpsLoaderComponent } from '../mps-loader/mps-loader.component';
import { MpsNavigationComponent } from '../mps-navigation/mps-navigation.component';

@Component({
  selector: 'mps-app-root',
  templateUrl: './mps-app.component.html',
  styleUrl: './mps-app.component.scss',
  imports: [
    MpsLoaderComponent,
    MpsHeaderComponent,
    MpsFooterComponent,
    MpsNavigationComponent,
    AdminViewAsBannerComponent,
    TranslateModule,
    MatSidenavModule,
    RouterOutlet,
  ],
})
export class MpsAppComponent {
  private readonly _matIconRegistry = inject(MatIconRegistry);
  private readonly _domSanitizer = inject(DomSanitizer);
  private readonly _translateService = inject(TranslateService);
  private readonly _userService = inject(MpsUserService);
  private readonly _dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly _user = signal(this._userService.getUserSubject().getValue());

  public constructor() {
    this.registerSvgIcon();
    this.initTranslationService();
    this._dateAdapter.setLocale(this._translateService.currentLang || environment.defaultLocale);
  }

  private initTranslationService() {
    this._translateService.setDefaultLang(environment.defaultLocale);
    this._translateService.addLangs(environment.allowedLanguage);

    // Set a default language if there's no language set
    if (!this._translateService.currentLang) {
      const supportedLangs = this._translateService.getLangs();
      const userLang = this._user()?.lang;
      const storedLang = localStorage.getItem('lang');

      let lang: string;
      let detectedFromBrowser = false;

      if (userLang && supportedLangs.includes(userLang)) {
        lang = userLang;
      } else if (storedLang && supportedLangs.includes(storedLang)) {
        lang = storedLang;
      } else {
        const browserLang = this.getBrowserLang();
        if (browserLang) {
          lang = browserLang;
          detectedFromBrowser = true;
        } else {
          lang = environment.defaultLocale;
        }
      }

      this._translateService.use(lang);

      // Persist browser-detected language so future visits use the saved preference
      if (detectedFromBrowser) {
        localStorage.setItem('lang', lang);
        const user = this._user();
        if (user) {
          this._userService.changeLang(user.id, lang).subscribe();
        }
      }
    }
  }

  private getBrowserLang(): string | null {
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && this._translateService.getLangs().includes(browserLang)) {
      return browserLang;
    }
    return null;
  }

  onSidenavToggle(opened: boolean): void {
    document.documentElement.classList.toggle('sidenav-open', opened);
  }

  private registerSvgIcon() {
    const icons = [...environment.mpsIcons, ...environment.appIcons];

    for (const icon of icons) {
      this._matIconRegistry.addSvgIcon(
        `total-${icon}`,
        this._domSanitizer.bypassSecurityTrustResourceUrl(`../../../assets/icons/${icon}.svg`)
      );
    }
  }
}
